import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, ListTodo, Trash2, CheckCircle2, Clock, AlertTriangle } from "lucide-react";

const PRIORITY = { low: "bg-slate-500/15 text-slate-600 dark:text-slate-300", medium: "bg-amber-500/15 text-amber-600", high: "bg-destructive/15 text-destructive" };
const STATUS = { open: "bg-blue-500/15 text-blue-600", in_progress: "bg-amber-500/15 text-amber-600", done: "bg-emerald-500/15 text-emerald-600" };
const STATUS_LABEL = { open: "Open", in_progress: "In Progress", done: "Done" };
const ASSIGN_ROLES = [
  { key: "regional", label: "Regional Manager" },
  { key: "gm", label: "General Manager" },
  { key: "sales", label: "Sales" },
];
const EMPTY = { title: "", details: "", assign_type: "role", assignee_role: "regional", assignee_user_id: "", property_id: "", due_date: "", priority: "medium" };
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "No due date");

export default function Tasks() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canCreate = isAdmin || user?.role === "regional" || !!user?.can_create_tasks;
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [scope, setScope] = useState(isAdmin ? "all" : (canCreate ? "created" : "mine"));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [props, setProps] = useState([]);
  const [users, setUsers] = useState([]);
  const [noteDrafts, setNoteDrafts] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    const params = { scope };
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/tasks", { params })
      .then((r) => setTasks(r.data))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [scope, statusFilter]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canCreate) return;
    api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data.filter((u) => ["regional", "gm", "sales"].includes(u.role)))).catch(() => {});
  }, [canCreate]);

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Add a title");
    setSaving(true);
    try {
      const payload = { ...form, due_date: form.due_date || null };
      if (form.assign_type === "role") payload.assignee_user_id = null;
      else payload.assignee_role = null;
      if (!payload.property_id) payload.property_id = null;
      await api.post("/tasks", payload);
      toast.success("Task assigned");
      setOpen(false); setForm(EMPTY); load();
      window.dispatchEvent(new Event("comms-refresh"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const updateTask = async (id, status, note) => {
    try {
      await api.patch(`/tasks/${id}`, { status, note: note || undefined });
      toast.success(status === "done" ? "Marked done" : "Task updated");
      setNoteDrafts((d) => ({ ...d, [id]: "" }));
      load();
      window.dispatchEvent(new Event("comms-refresh"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try { await api.delete(`/tasks/${id}`); load(); window.dispatchEvent(new Event("comms-refresh")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const assignedLabel = (t) => t.assign_type === "user"
    ? (t.assignee_name || "user")
    : `All ${ASSIGN_ROLES.find((r) => r.key === t.assignee_role)?.label || t.assignee_role}${t.property_name ? " · " + t.property_name : ""}`;

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold" data-testid="tasks-heading">Tasks</h1>
          <p className="text-sm text-muted-foreground">{canCreate ? "Assign tasks and track completion across your team." : "Tasks assigned to you — update status until each is done."}</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin ? (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="h-9 w-40" data-testid="tasks-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tasks</SelectItem>
                <SelectItem value="mine">Assigned to me</SelectItem>
              </SelectContent>
            </Select>
          ) : canCreate ? (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="h-9 w-44" data-testid="tasks-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="created">Created by me</SelectItem>
                <SelectItem value="mine">Assigned to me</SelectItem>
              </SelectContent>
            </Select>
          ) : null}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-36" data-testid="tasks-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="done">Done</SelectItem>
            </SelectContent>
          </Select>
          {canCreate && (
            <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="tasks-new-btn"><Plus className="mr-2 h-4 w-4" /> New Task</Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : tasks.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-16 text-center" data-testid="tasks-empty">
          <ListTodo className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{canCreate ? "No tasks yet. Create one to assign it to your managers." : "You're all caught up — no tasks assigned."}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((t) => (
            <Card key={t.id} className={`p-4 ${t.overdue ? "border-destructive/60" : ""}`} data-testid={`task-${t.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-sm px-2 py-0.5 text-[0.65rem] font-bold uppercase ${STATUS[t.status]}`} data-testid={`task-status-${t.id}`}>{STATUS_LABEL[t.status]}</span>
                    <span className={`rounded-sm px-2 py-0.5 text-[0.65rem] font-bold uppercase ${PRIORITY[t.priority]}`}>{t.priority}</span>
                    {t.overdue && <span className="inline-flex items-center gap-1 text-[0.7rem] font-semibold text-destructive"><AlertTriangle className="h-3.5 w-3.5" /> Overdue</span>}
                    <h3 className="font-heading text-lg font-bold">{t.title}</h3>
                  </div>
                  {t.details && <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{t.details}</p>}
                  <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                    <span>👤 {assignedLabel(t)}</span>
                    <span className={t.overdue ? "font-semibold text-destructive" : ""}><Clock className="mr-1 inline h-3.5 w-3.5" />{fmtDate(t.due_date)}</span>
                    <span>Assigned by {t.created_by}</span>
                  </div>
                </div>
                {(isAdmin || t.created_by_id === user?.id) && (
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => del(t.id)} data-testid={`task-del-${t.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                )}
              </div>

              {(t.notes || []).length > 0 && (
                <div className="mt-3 space-y-1 rounded-md bg-secondary/50 p-2">
                  {t.notes.map((n, i) => (
                    <div key={i} className="text-xs">
                      <span className="font-semibold">{n.by}</span>
                      {n.status_change && <span className="ml-1 text-muted-foreground">→ {STATUS_LABEL[n.status_change]}</span>}
                      {n.text && <span className="ml-1">— {n.text}</span>}
                    </div>
                  ))}
                </div>
              )}

              {t.status !== "done" && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Input placeholder="Add an update note (optional)…" value={noteDrafts[t.id] || ""}
                    onChange={(e) => setNoteDrafts((d) => ({ ...d, [t.id]: e.target.value }))}
                    className="h-9 max-w-md" data-testid={`task-note-${t.id}`} />
                  {t.status === "open" && (
                    <Button size="sm" variant="outline" onClick={() => updateTask(t.id, "in_progress", noteDrafts[t.id])} data-testid={`task-start-${t.id}`}>Start</Button>
                  )}
                  <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => updateTask(t.id, "done", noteDrafts[t.id])} data-testid={`task-done-${t.id}`}>
                    <CheckCircle2 className="mr-1 h-4 w-4" /> Mark Done
                  </Button>
                </div>
              )}
              {t.status === "done" && (
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">✓ Completed{t.completed_at ? ` on ${new Date(t.completed_at).toLocaleDateString()}` : ""}</span>
                  <Button size="sm" variant="ghost" onClick={() => updateTask(t.id, "open")} data-testid={`task-reopen-${t.id}`}>Reopen</Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create task dialog (admin) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="task-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New Task</DialogTitle>
            <DialogDescription>Assign a task to a manager or a whole role.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="space-y-1"><Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Upload STAR Report" data-testid="task-form-title" />
            </div>
            <div className="space-y-1"><Label>Details</Label>
              <Textarea value={form.details} onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))} placeholder="What needs to be done…" rows={3} data-testid="task-form-details" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Assign to</Label>
                <Select value={form.assign_type} onValueChange={(v) => setForm((f) => ({ ...f, assign_type: v }))}>
                  <SelectTrigger data-testid="task-form-assigntype"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="role">A role</SelectItem>
                    <SelectItem value="user">A specific person</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.assign_type === "role" ? (
                <div className="space-y-1"><Label>Role</Label>
                  <Select value={form.assignee_role} onValueChange={(v) => setForm((f) => ({ ...f, assignee_role: v }))}>
                    <SelectTrigger data-testid="task-form-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ASSIGN_ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-1"><Label>Person</Label>
                  <Select value={form.assignee_user_id} onValueChange={(v) => setForm((f) => ({ ...f, assignee_user_id: v }))}>
                    <SelectTrigger data-testid="task-form-user"><SelectValue placeholder="Pick a manager" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email} ({u.role})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Property {form.assign_type === "role" ? "(optional)" : ""}</Label>
                <Select value={form.property_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, property_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="task-form-property"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Any / All properties</SelectItem>
                    {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger data-testid="task-form-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1"><Label>Due date (optional)</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="task-form-due" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="task-form-submit">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Assign Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
